'use client';

import { Button } from '@pickleballinc/react-ui';
import { ReactNode, useEffect, useRef, useState } from 'react';

interface DetailsCardProps {
	title: string;
	subtitle?: ReactNode | string;
	content: ReactNode | string;
	expandable?: boolean;
	expandedByDefault?: boolean;
	isSubtitleCta?: boolean;
}

export const DetailsCard = (props: DetailsCardProps) => {
	const { title, subtitle, content, expandable = true, expandedByDefault = false, isSubtitleCta = false } = props;

	const [showButton, setShowButton] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (contentRef.current) {
			// Check if content height exceeds the specified height
			if (contentRef.current.scrollHeight > 48 * 4) {
				setShowButton(true);
			}
		}
	}, [content]);

	const [isExpanded, setIsExpanded] = useState(expandedByDefault);

	const handleToggle = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<div className={`relative flex flex-col gap-4 rounded-lg bg-white p-4 sm:gap-6 sm:p-8 ${expandable ? 'pb-10 sm:pb-12' : ''} `}>
			{(title || subtitle) && (
				<div className="flex flex-row items-center justify-between">
					<div className="text-lg font-bold text-blue-600 sm:text-2xl">{title}</div>
					<div className={`${isSubtitleCta ? 'hidden sm:block' : ''}`}>{subtitle}</div>
				</div>
			)}

			<div ref={contentRef} className={`${!expandable || isExpanded || !showButton ? 'h-auto' : 'h-48 overflow-hidden text-gray-900'}`}>
				{content}
			</div>
			{expandable && !isExpanded && (
				<div
					className="pointer-events-none absolute inset-x-0 bottom-0 h-32 rounded-b-lg"
					style={{
						background: showButton ? 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))' : ''
					}}
				/>
			)}
			{subtitle && isSubtitleCta && <div className="ml-auto sm:hidden">{subtitle}</div>}
			{expandable && showButton && (
				<Button onClick={handleToggle} className="absolute bottom-4 right-4 sm:right-6" variant="link-gray">
					{isExpanded ? 'See Less' : 'See More'}
				</Button>
			)}
		</div>
	);
};
